import {logAiSearchInteraction} from 'analytics/aiSearchAnalytics';
import {
  AiQueryAssistantState,
  AiQueryTurn,
  emptyAssistant,
  newTurnId,
  pendingCall,
  settlePendingCall,
} from 'components/ai/aiQueryConversation';
import {SpeculativeAdoptError, type SpeculativeRun} from 'components/ai/speculativeAsk';
import type {ChatMention} from 'components/DSYS/chat/chatMentions';
import type {ProfileAskKind} from 'components/profile/profileAsk';
import {type Dispatch, type SetStateAction, useCallback, useRef} from 'react';
import {
  type AiQueryResult,
  type AiQueryStreamCallbacks,
  streamAiQuery,
} from 'views/admin/adminAiQueryApi';

type TurnUpdater = (
  turnId: string,
  patch:
    | Partial<AiQueryAssistantState>
    | ((a: AiQueryAssistantState) => Partial<AiQueryAssistantState>)
) => void;

export type AiQueryConversationConfig = {
  elevated: boolean;
  enableSourcegraph: boolean;
  turns: AiQueryTurn[];
  setTurns: Dispatch<SetStateAction<AiQueryTurn[]>>;
  conversationId: string | null;
  setConversationId: Dispatch<SetStateAction<string | null>>;
  isStreaming: boolean;
  setIsStreaming: Dispatch<SetStateAction<boolean>>;
};

export const useAiQueryConversation = ({
  elevated,
  enableSourcegraph,
  turns,
  setTurns,
  conversationId,
  setConversationId,
  isStreaming,
  setIsStreaming,
}: AiQueryConversationConfig) => {
  const abortRef = useRef<AbortController | null>(null);
  // Monotonic id for the active run so an aborted stream's late callbacks (e.g.
  // when starting a fresh chat mid-stream) can't clobber the new run's state.
  const runIdRef = useRef(0);

  const updateAssistant: TurnUpdater = useCallback(
    (turnId, patch) =>
      setTurns((prev) =>
        prev.map((t) =>
          t.id === turnId
            ? {
                ...t,
                assistant: {
                  ...t.assistant,
                  ...(typeof patch === 'function' ? patch(t.assistant) : patch),
                },
              }
            : t
        )
      ),
    [setTurns]
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    // Abort any in-flight stream so a new chat can start immediately.
    abortRef.current?.abort();
    setTurns([]);
    setConversationId(null);
  }, [setConversationId, setTurns]);

  const ask = useCallback(
    async (
      question: string,
      opts?: {
        fresh?: boolean;
        mentions?: ChatMention[];
        /** A turn search started while the question was typed; taken over instead of a new one. */
        speculativeRun?: SpeculativeRun | null;
        /** The Knowledge page a chip's question came from; the server reads its articles first. */
        knowledgePageId?: string;
        /** The profile chip the question came from; the server prefetches for it. */
        profileAsk?: ProfileAskKind;
      }
    ) => {
      const q = question.trim();
      // A fresh ask starts a brand-new conversation and may interrupt an
      // in-flight stream; a normal ask is ignored while streaming.
      if (!q || (isStreaming && !opts?.fresh)) {
        return;
      }

      const runId = ++runIdRef.current;
      const turnId = newTurnId();
      // Index of this turn within the conversation, captured for analytics on
      // the agent's tool calls (which fire later during streaming).
      let turnIndex = 0;
      // Use a local conversation id so a fresh ask doesn't depend on the
      // (possibly stale) state value still pointing at the old conversation.
      let activeConversationId = conversationId;
      const mentions = opts?.mentions?.length ? opts.mentions : undefined;
      const turn: AiQueryTurn = {
        id: turnId,
        question: q,
        mentions,
        assistant: emptyAssistant(true),
      };
      if (opts?.fresh) {
        abortRef.current?.abort();
        activeConversationId = null;
        setConversationId(null);
        setTurns([turn]);
      } else {
        setTurns((prev) => {
          turnIndex = prev.length;
          return [...prev, turn];
        });
      }
      setIsStreaming(true);
      const startedAt = Date.now();

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const callbacks: AiQueryStreamCallbacks = {
          signal: controller.signal,
          elevated,
          enableSourcegraph,
          mentionedPeople: mentions,
          knowledgePageId: opts?.knowledgePageId,
          profileAsk: opts?.profileAsk,
          conversationId: activeConversationId ?? undefined,
          onConversation: (id) => {
            // Keep the local id in sync so tool-call analytics on a fresh
            // conversation carry the server-minted id, not undefined.
            activeConversationId = id;
            if (runIdRef.current === runId) {
              setConversationId(id);
            }
          },
          onTurn: (datasetTurnId) => updateAssistant(turnId, {datasetTurnId, elevated}),
          onText: (delta, parent) => {
            // A sub-agent's text (Dash's findings) is for the main model, not the answer.
            if (parent) {
              return;
            }
            updateAssistant(turnId, (a) => ({
              answer: a.answer + delta,
              thoughtSeconds:
                a.thoughtSeconds ?? Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
            }));
          },
          onToolCall: (call) => {
            // Log every tool the agent selects (not just ones the user expands)
            // so we can see which capabilities are actually used vs never picked.
            logAiSearchInteraction({
              action: 'tool_call',
              toolName: call.name,
              turnIndex,
              conversationId: activeConversationId ?? undefined,
            });
            updateAssistant(turnId, (a) => ({trace: settlePendingCall(a.trace, call)}));
          },
          // A call with a long argument (a drafted update) can take many seconds
          // to write, so show its step as soon as the model starts it.
          onToolStarted: (name) =>
            updateAssistant(turnId, (a) => ({trace: [...a.trace, pendingCall(name)]})),
          onToolProgress: (progress) =>
            updateAssistant(turnId, (a) => ({
              trace: [...a.trace, {kind: 'progress' as const, progress}],
            })),
          onToolResult: (result) =>
            updateAssistant(turnId, (a) => ({trace: [...a.trace, {kind: 'result', result}]})),
          onDataset: (dataset) =>
            updateAssistant(turnId, (a) => ({
              datasets: {...a.datasets, [dataset.handle ?? dataset.capability]: dataset},
            })),
        };
        let final: AiQueryResult;
        try {
          final = opts?.speculativeRun
            ? await opts.speculativeRun.adopt(callbacks)
            : await streamAiQuery(q, callbacks);
        } catch (e) {
          // The speculative turn ended before it was sent: ask from scratch.
          if (!(e instanceof SpeculativeAdoptError)) {
            throw e;
          }
          final = await streamAiQuery(q, callbacks);
        }
        updateAssistant(turnId, (a) => ({
          results: final.results,
          answer: final.answer_markdown || a.answer,
          recipe: final.recipe ?? null,
        }));
        // One event per completed turn so we can track the share of turns that
        // got a full answer over time (and break down the gaps by status).
        const gap = final.answer_gap ?? null;
        logAiSearchInteraction({
          action: 'turn_complete',
          turnIndex,
          conversationId: activeConversationId ?? undefined,
          fullyAnswered: !gap,
          durationMs: Date.now() - startedAt,
          answerGapStatus: gap?.status,
          answerGapScope: gap?.scope,
          answerGapMissing: gap?.missing,
        });
      } catch (e) {
        if (!(e instanceof DOMException && e.name === 'AbortError')) {
          updateAssistant(turnId, {error: e instanceof Error ? e.message : String(e)});
        }
      } finally {
        updateAssistant(turnId, (a) => ({
          isStreaming: false,
          thoughtSeconds:
            a.thoughtSeconds ?? Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
        }));
        // Only the most recent run owns the shared streaming/abort state; a run
        // superseded by a fresh ask must not reset it.
        if (runIdRef.current === runId) {
          setIsStreaming(false);
          abortRef.current = null;
        }
      }
    },
    [
      conversationId,
      elevated,
      enableSourcegraph,
      isStreaming,
      setConversationId,
      setIsStreaming,
      setTurns,
      updateAssistant,
    ]
  );

  return {
    turns,
    isStreaming,
    ask,
    stop,
    reset,
  };
};
